import { FC, ChangeEvent, useState, useEffect, useMemo } from 'react';
import {
  Divider,
  Box,
  FormControl,
  InputLabel,
  Card,
  Table,
  TableBody,
  TableHead,
  TablePagination,
  TableRow,
  TableContainer,
  Select,
  MenuItem,
  Typography,
  TextField,
  Grid,
  Button,
  InputAdornment,
  Tooltip,
  ListSubheader
} from '@mui/material';

import { EmployeeDetail, SearchEmployee } from 'src/models/employee';
import {
  downloadEmployeeReport,
  getAllBillingRoles,
  searchEmployee,
  getEmployeeById,
  getEmployeeList
} from 'src/services/employeeService';
import styled from '@emotion/styled';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useDispatch, useSelector } from 'react-redux';
import { RootStateType } from 'src/store/RootReducer';
import { useNavigate } from 'react-router';
import AddTwoToneIcon from '@mui/icons-material/AddTwoTone';
import EmployeeAddForm from './EmployeeAddForm';
import LoginIcon from '@mui/icons-material/Login';
import { useForm } from 'react-hook-form';
import './Employee.css';
import { withStyles } from '@mui/styles';
import SearchIcon from '@mui/icons-material/Search';
import GetAppIcon from '@mui/icons-material/GetApp';
import ImportModal from './components/ImportEmployees/ImportModal';
import ImportBankingModal from './components/ImportBankingInformation/ImportBankingModal';
import fileDownload from 'js-file-download';
import { TableColumnCell } from 'src/components/TableColumnCell';
import { TableHeaderCell } from 'src/components/TableHeaderCell';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import moment from 'moment';
import { AppActions } from 'src/store/AppReducer';

interface EmployeeTableProps {
  permissions: any;
}

const EmployeeTable: FC<EmployeeTableProps> = ({ permissions }) => {
    const navigate = useNavigate();
  const dispatch = useDispatch();

  const [open, setOpen] = useState(false);
  const [openImport, setOpenImport] = useState(false);
  const [openBankingImport, setOpenBankingImport] = useState(false);

  const [isDisabled, setIsDisabled] = useState(false);

  //disabled download button , search and filter option
  const [isDisabledDownloadReport, setIsDisabledDownloadReport] = useState<boolean>(true);
  const [total, setTotal] = useState<number>(1);

  const employeeId = useSelector((state: RootStateType) => state.appReducer.user.EmployeeId);
  const userRole = useSelector((state: RootStateType) => state.appReducer.user.Roles);
  const [designationSearchText, setDesignationSearchText] = useState<string>('');

  var initalList: EmployeeDetail[] = [];
  let initialEmployeeFilterValues = {
    name: null,
    tier: null,
    designation: null,
    trackCode: null,
    employmentStatus: null,
    employmentType: null,
    size: 10,
    page: 0
  } as SearchEmployee;

  const [employeeData, setEmployeeData] = useState<EmployeeDetail[]>(initalList);
  const [empFilter, setEmpFilter] = useState<SearchEmployee>(initialEmployeeFilterValues);
  const [toggleFilter, setToggleFilter] = useState(false);

  const [billingRoles, setBillingRoles] = useState<any>([]);

  const { register, handleSubmit, reset, watch, setValue } =
    useForm<SearchEmployee>({
      defaultValues: initialEmployeeFilterValues
    });

  const designation = watch('designation');
  const tier = watch('tier');
  const employmentType = watch('employmentType');
  const trackCode = watch('trackCode');
  const employmentStatus = watch('employmentStatus');

  const tracks = useSelector(
    (state: RootStateType) => state.employeeReducer.tracks
  );

  useEffect(() => {
    getAllBillingRoles().then((res) => setBillingRoles(res.data));
  }, []);

  const containsText = (text, searchText) =>
    text?.toLowerCase().indexOf(searchText?.toLowerCase()) > -1;

  const filteredBillingRoles = useMemo(
    () =>
      billingRoles?.length > 0 &&
    billingRoles?.filter((option) => containsText(option?.name, designationSearchText)),
    [billingRoles, designationSearchText]
  );

  const toggleImport = () => {
    setOpenImport(!openImport);
  };

  const toggleBankingImport = () => {
    setOpenBankingImport(!openBankingImport);
  };

  const handlePageChange = (event: any, newPage: number): void => {
    const newFilter = {
      ...empFilter,
      page: newPage
    }
    setEmpFilter(newFilter);
  };

  const handleLimitChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const newFilter = {
      ...empFilter,
      page: 0,
      size: parseInt(event.target.value)
    }
    setEmpFilter(newFilter);
  };

  useEffect(() => {   
    filterEmployee();
  }, [empFilter, userRole, employeeId]);

  const filterEmployee = async () => {
    dispatch(AppActions.showLoader());
    const user_role = userRole.split(',');
    if(userRole) {
      if(user_role.includes('Super User') || user_role.includes('HR Manager') || user_role.includes('Assistant HR Manager') || user_role.includes('HR Executive') || user_role.includes('Operations')) {
        setIsDisabledDownloadReport(false);
        await getEmployeeList(empFilter).then((res) => {
          setEmployeeData((res?.data?.employees as EmployeeDetail[]) || []);
          setTotal(res?.data?.totalCount as number);
          dispatch(AppActions.hideLoader());
        });
      }else{
        setIsDisabledDownloadReport(true);
        getEmployeeById(employeeId).then((res) => {
          setEmployeeData([res.data]);
          setTotal(1);
          dispatch(AppActions.hideLoader());
        });
      }
    }
  };

  const getTableData = () => {
    filterEmployee();
  };

  useEffect(() => {
    getTableData();
  }, [userRole]);

  //when user started to type the name in the text field
  const nameChange = (event) => {
    const newFilter = {
      ...empFilter,
      page: 0,
      name: event.target.value
    }
    setEmpFilter(newFilter);
  };

  const NavigateToEditSection = (employeeId: string, view) => {
    navigate('edit-employee/' + employeeId);
    navigate(`edit-employee/${employeeId}?view=${view}`);
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const onSubmit = (data) => {
    setEmpFilter(data);
  };

  const resetFilterResult = () => {
    reset();
    setEmpFilter(initialEmployeeFilterValues);
  };

  useEffect(() => {
    const isNullish = Object.values(empFilter).every((value) => {
      if (value === null) {
        return true;
      }

      return false;
    });

    if (Object.keys(empFilter)?.length === 0 || isNullish) {
      setIsDisabled(true);
    } else {
      setIsDisabled(false);
    }
  }, [empFilter]);

  // custom table row with different color
  const CustomTableRow = withStyles({
    root: {
      background: '#E6E9FF !important',
      height: '3.5rem'
    }
  })(TableRow);

  const downloadReport = () => {
    downloadEmployeeReport().then((res) => {
      fileDownload(res.data, 'employee-report.xlsx');
    });
  };

  const converExperience = (data) => {
    let experience = '';
    if(data?.trim() === '0Y 0M'){
      experience = 'No Experience';
    } else {
      const dataArray = data.split(' ');
      const year = Number(dataArray[0].substring(0, dataArray[0]?.length - 1));
      const month = Number(dataArray[1].substring(0, dataArray[1]?.length - 1));

      if(year > 0 && month > 0){
        experience = year + (year === 1 ? ' year ' : ' years ') + month + (month === 1 ? ' month' : ' months');
      } else{
        if(year === 0 && month > 0){
          experience = month + (month === 1 ? ' month' : ' months');
        }
        if(month === 0 && year > 0){
          experience = year + (year === 1 ? ' year' : ' years');
        }
      }
    }

    return experience;
  }

  return (
    <>
      <div>
        <iframe
          title="frame"
          id="my_iframe"
          style={{ display: 'none' }}
        ></iframe>
        <Grid container sx={{ mt: 2, mb: 2 }} justifyContent="space-between">
          <Grid item>
            <Typography
              style={{
                marginLeft: '0px',
                fontWeight: '700',
                fontSize: '16px',
                fontFamily:
                  'Inter,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol"'
              }}
            >
              Employee List
            </Typography>
            <Typography variant="subtitle2"></Typography>
          </Grid>
          <Grid item>
            <Button
              disabled={isDisabledDownloadReport}
              sx={{
                mr: 2,
                mt: { xs: 2, md: 0 },
                fontSize: '11px',
                fontWeight: 'normal'
              }}
              variant="contained"
              startIcon={<GetAppIcon fontSize="small" />}
              onClick={() => downloadReport()}
              style={{ height: '1.75rem' }}
              id="btn-downloadReport"
            >
              Download Report
            </Button>
            <Button
              sx={{
                mr: 2,
                mt: { xs: 2, md: 0 },
                fontSize: '11px',
                fontWeight: 'normal'
              }}
              variant="contained"
              startIcon={<LoginIcon fontSize="small" />}
              onClick={() => toggleImport()}
              style={{ height: '1.75rem' }}
              id="btn-toggleImport"
              disabled={
                !permissions.some(
                  (perm) => perm.name === 'Create New' && perm.value === true
                )
              }
            >
              Import Employee Information
            </Button>

            <ImportModal
              open={openImport}
              toggle={toggleImport}
              getData={getTableData}
            />

            <ImportBankingModal
              open={openBankingImport}
              toggle={toggleBankingImport}
              getData={getTableData}
            />

            <Button
              sx={{
                mt: { xs: 2, md: 0 },
                fontSize: '11px',
                fontWeight: 'normal'
              }}
              variant="contained"
              id="btn-handleClickOpen"
              startIcon={<AddTwoToneIcon fontSize="small" />}
              onClick={handleClickOpen}
              style={{ height: '1.75rem' }}
              disabled={
                !permissions.some(
                  (perm) => perm.name === 'Create New' && perm.value === true
                )
              }
            >
              Create New
            </Button>
            <EmployeeAddForm
              open={open}
              onClose={handleClose}
              getData={getTableData}
            />
          </Grid>
        </Grid>
      </div>
      <Card>
        {!isDisabledDownloadReport && (
          <Grid sx={{ px: 2, py: 2 }} item xs={6}>
            <TextField
              sx={{ width: 320, mr: 2 }}
              size="small"
              id="mainSearchEmployee"
              placeholder="Employee Name"
              onChange={(e) => nameChange(e)}
              InputProps={{
                style: { fontSize: '12px', height: '1.75rem' },
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      sx={{ color: '#d3d3d3' }}
                      style={{ fontSize: '15px' }}
                    />
                  </InputAdornment>
                )
              }}
            />
            <Button
              id="btn-setToggleFilter"
              sx={{
                mt: { xs: 2, md: 0 },
                color: `${!toggleFilter && 'gray'}`
              }}
              variant="outlined"
              startIcon={<FilterListIcon style={{ fontSize: '15px' }} />}
              onClick={() => {
                setToggleFilter(!toggleFilter);
              }}
              style={{ height: '1.75rem', fontSize: '12px' }}
            >
              Filters
            </Button>
          </Grid>
        )}
        {toggleFilter && (
          <Grid px={2} pb={2} style={{margin: 0}}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid
                container
                spacing={2}
                style={{
                  paddingTop: 2,
                  paddingBottom: 2,
                  justifyContent: 'between'
                }}
              >
                <Grid
                  item
                  xs={3}
                  style={{
                    maxWidth: '16.2rem'
                  }}
                >
                  <FormControl fullWidth>
                    <InputLabel
                      style={{
                        fontSize: '12px'
                      }}
                    >
                      Designation
                    </InputLabel>
                    <Select
                      id="designation"
                      sx={{ mt: 1 }}
                      style={{
                        fontSize: '12px',
                        height: '2rem'
                      }}
                      fullWidth
                      size="small"
                      {...register('designation')}
                      value={designation||''}
                      onChange={(e) => {
                        setValue('designation', e.target.value);
                      }}
                      onClose={() => setDesignationSearchText('')}
                    >
                      <ListSubheader>
                        <TextField
                          id="search-designation"
                          size="small"
                          autoFocus
                          placeholder="Type to search..."
                          fullWidth
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon />
                              </InputAdornment>
                            )
                          }}
                          onChange={(e) => setDesignationSearchText(e.target.value)}
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                          onKeyDown={(e) => {
                            if (e.key !== 'Escape') {
                              e.stopPropagation();
                            }
                          }}
                        ></TextField>
                      </ListSubheader>
                      {(filteredBillingRoles || []).map((item, i) => {
                        return (
                          <MenuItem value={item.name} key={i}>
                            {item.name}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid
                  item
                  xs={3}
                  style={{
                    maxWidth: '16.2rem'
                  }}
                >
                  <FormControl fullWidth>
                    <InputLabel
                      style={{
                        fontSize: '12px'
                      }}
                    >
                      Tier
                    </InputLabel>
                    <Select
                      sx={{ mt: 1 }}
                      id="tier"
                      style={{
                        fontSize: '12px',
                        height: '2rem'
                      }}
                      fullWidth
                      size="small"
                      value={tier ||''}
                      {...register('tier')}
                      onChange={(e) => {
                        setValue('tier', e.target.value);
                      }}
                    >
                      <MenuItem value={'T0'} key={'T0'}>
                        T0
                      </MenuItem>
                      <MenuItem value={'T1'} key={'T1'}>
                        T1
                      </MenuItem>
                      <MenuItem value={'T2'} key={'T2'}>
                        T2
                      </MenuItem>
                      <MenuItem value={'T3'} key={'T3'}>
                        T3
                      </MenuItem>
                      <MenuItem value={'T4'} key={'T4'}>
                        T4
                      </MenuItem>
                      <MenuItem value={'T5'} key={'T5'}>
                        T5
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid
                  item
                  xs={3}
                  style={{
                    maxWidth: '16.2rem'
                  }}
                >
                  <FormControl fullWidth>
                    <InputLabel
                      style={{
                        fontSize: '12px'
                      }}
                    >
                      Status
                    </InputLabel>
                    <Select
                      sx={{ mt: 1 }}
                      id="employmentType"
                      style={{
                        fontSize: '12px',
                        height: '2rem'
                      }}
                      fullWidth
                      size="small"
                      value={employmentType ||''}
                      {...register('employmentType')}
                      onChange={(e) => {
                        setValue('employmentType', e.target.value);
                      }}
                    >
                      <MenuItem value={'Permanent'} key={'Permanent'}>
                        Permanent
                      </MenuItem>
                      <MenuItem value={'Probation'} key={'Probation'}>
                        Probation
                      </MenuItem>
                      <MenuItem value={'Intern'} key={'Intern'}>
                        Intern
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid
                  item
                  xs={3}
                  style={{
                    maxWidth: '16.2rem'
                  }}
                >
                  <FormControl fullWidth>
                    <InputLabel
                      style={{
                        fontSize: '12px'
                      }}
                    >
                      Track Code
                    </InputLabel>
                    <Select
                      sx={{ mt: 1 }}
                      style={{
                        fontSize: '12px',
                        height: '2rem'
                      }}
                      fullWidth
                      size="small"
                      id="trackCode"
                      value={trackCode ||''}
                      {...register('trackCode')}
                      onChange={(e) => {
                        setValue('trackCode', e.target.value);
                      }}
                    >
                      {(tracks || []).map((item, i) => {
                        return (
                          <MenuItem value={item.code} key={i}>
                            {item.code}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid
                  item
                  xs={3}
                  style={{
                    maxWidth: '16.2rem'
                  }}
                >
                  <FormControl fullWidth>
                    <InputLabel
                      style={{
                        fontSize: '12px'
                      }}
                    >
                      Employment Status
                    </InputLabel>
                    <Select
                      sx={{ mt: 1 }}
                      style={{
                        fontSize: '12px',
                        height: '2rem'
                      }}
                      fullWidth
                      size="small"
                      id="employmentStatus"
                      value={employmentStatus ||''}
                      {...register('employmentStatus')}
                      onChange={(e) => {
                        setValue('employmentStatus', e.target.value);
                      }}
                    >
                      <MenuItem value={'Active'}>Active</MenuItem>
                      <MenuItem value={'Inactive'}>Inactive</MenuItem>
                      <MenuItem value={'All'}>All</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid
                  container
                  style={{
                    justifyContent: 'flex-end',
                    marginTop: 16,
                    marginLeft: 'auto'
                  }}
                  xs={9}
                >
                  <Button
                    sx={{ mt: { xs: 2, md: 0 }, mr: 1 }}
                    variant="outlined"
                    onClick={resetFilterResult}
                    disabled={isDisabled}
                    className={isDisabled &&'clear-btn'}
                    style={{ height: '1.75rem', fontSize: '12px' }}
                    id="btn-resetFilterResult"
                  >
                    Clear
                  </Button>
                  <Button
                    sx={{ mt: { xs: 2, md: 0 } }}
                    id="btn-onSubmit"
                    variant="outlined"
                    type="submit"
                    disabled={isDisabled}
                    className={isDisabled &&'filter-btn'}
                    style={{ height: '1.75rem', fontSize: '12px' }}
                  >
                    Filter
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Grid>
        )}
        <Divider />
        <TableContainer className="tableContainer">
          <Table style={{ borderCollapse: 'separate' }} size="small">
            <TableHead>
              <CustomTableRow>
                <TableHeaderCell content="Name" smallFont />
                <TableHeaderCell content="Designation" smallFont />
                <TableHeaderCell content="Email address" smallFont />
                <TableHeaderCell content="Join Date" smallFont />
                <TableHeaderCell content="Total Years of experience" smallFont />
                <TableHeaderCell content="Digiratina experience" smallFont />
                <TableHeaderCell content="Status" smallFont />
                <TableHeaderCell sticky content="Actions" smallFont />
              </CustomTableRow>
            </TableHead>

            <TableBody>
              {employeeData &&
                employeeData?.length > 0 &&
                employeeData.map((employee, i) => {
                  return (
                    <TableRow
                      hover
                      key={employee.employeeId}
                    >
                      <TableColumnCell>
                        <Typography
                          paddingBottom="0.25rem"
                          paddingTop="0.5rem"
                          variant="body2"
                          style={{ fontSize: '11px' }}
                          gutterBottom
                          noWrap
                        >
                          {employee.name}
                        </Typography>
                      </TableColumnCell>
                      <TableColumnCell>
                        <Typography
                          paddingBottom="0.25rem"
                          paddingTop="0.5rem"
                          variant="body2"
                          style={{ fontSize: '11px' }}
                          color="text.primary"
                          gutterBottom
                          noWrap
                        >
                          {employee.designation}
                        </Typography>
                      </TableColumnCell>
                      <TableColumnCell>
                        <Typography
                          paddingBottom="0.25rem"
                          paddingTop="0.5rem"
                          variant="body2"
                          style={{ fontSize: '11px' }}
                          color="text.primary"
                          gutterBottom
                          noWrap
                        >
                          {employee.email}
                        </Typography>
                      </TableColumnCell>
                      <TableColumnCell>
                        <Typography
                          paddingBottom="0.25rem"
                          paddingTop="0.5rem"
                          variant="body2"
                          style={{ fontSize: '11px' }}
                          color="text.primary"
                          gutterBottom
                          noWrap
                        >
                          {moment(employee?.joinedDate?.split(` `)[0]).format(
                            'MM/DD/YYYY'
                          ) || '-'}
                        </Typography>
                      </TableColumnCell>
                      <TableColumnCell>
                        <Typography
                          paddingBottom="0.25rem"
                          paddingTop="0.5rem"
                          variant="body2"
                          style={{ fontSize: '11px' }}
                          color="text.primary"
                          gutterBottom
                          noWrap
                        >
                          {converExperience(employee.yearsOfExperience)}
                        </Typography>
                      </TableColumnCell>
                      <TableColumnCell>
                        <Typography
                          paddingBottom="0.25rem"
                          paddingTop="0.5rem"
                          variant="body2"
                          style={{ fontSize: '11px' }}
                          color="text.primary"
                          gutterBottom
                          noWrap
                        >
                          {converExperience(employee.companyExperience)}
                        </Typography>
                      </TableColumnCell>
                      <TableColumnCell>
                        <Typography
                          paddingBottom="0.25rem"
                          paddingTop="0.5rem"
                          variant="body2"
                          style={{
                            fontSize: '11px',
                            color: `${
                              !employee?.employeeFinance?.isResign
                                ? 'green'
                                : 'red'
                            }`
                          }}
                          color="text.primary"
                          gutterBottom
                          noWrap
                        >
                          {!employee?.employeeFinance?.isResign
                            ? 'Active'
                            : 'Inactive'}
                        </Typography>
                      </TableColumnCell>
                      <TableColumnCell sticky>
                        <div
                          style={{ background: 'white', marginLeft: 'auto' }}
                        >
                        {permissions.some(
                            (perm) =>
                              perm.name === 'Edit' && perm.value === true
                          ) && (
                          <Tooltip title="Edit" placement="top" >
                          <EditIcon
                            id={"btn-edit" + i}
                            onClick={() => {
                              if (
                                permissions.some(
                                  (perm) =>
                                    perm.name === 'Edit' && perm.value === true
                                )
                              ) {
                                NavigateToEditSection(
                                  employee.employeeId,
                                  false
                                );
                              }
                            }}
                            style={{
                              fontSize: '17px',
                              cursor: 'pointer'
                              // color: `${
                              //   !permissions.some(
                              //     (perm) =>
                              //       perm.name === 'Create New' &&
                              //       perm.value === true
                              //   )
                              //     ? '#dddddd'
                              //     : '#fffff'
                              // }`
                            }}
                          />
                          </Tooltip> 
                         )} 
                          <Tooltip title="View" placement="top" >
                          <VisibilityIcon
                            id={"btn-view" + i}
                            onClick={() => {
                              NavigateToEditSection(employee.employeeId, true);
                            }}
                            style={{
                              fontSize: '17px',
                              cursor: 'pointer'
                            }}
                          />
                          </Tooltip>
                        </div>
                      </TableColumnCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
        {total === 0 && (
          <Typography
            style={{
              fontSize: '15px',
              fontWeight: 'bold',
              margin: '1rem',
              textAlign: 'center'
            }}
            variant="body2"
          >
            No Records Found
          </Typography>
        )}
        <Box p={2}>
          <TablePagination
            sx={{
              fontSize: '12px',
              '.MuiTablePagination-displayedRows': {
                fontSize: '12px'
              },
              '.MuiTablePagination-selectLabel': {
                fontSize: '12px'
              }
            }}
            component="div"
            count={total}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleLimitChange}
            page={empFilter?.page}
            rowsPerPage={empFilter?.size}
            rowsPerPageOptions={[10, 20, 30, 40, 50]}
          />
        </Box>
      </Card>
    </>
  );
};

EmployeeTable.propTypes = {};

EmployeeTable.defaultProps = {};

export default EmployeeTable;
